const DB_NAME = "hkp-product-media-uploads";
const STORE = "jobs";

export type StoredUploadJob = {
  mediaId: string;
  mediaAssetId?: string;
  productId: string;
  ownerKey?: string;
  fileName: string;
  mime: string;
  section: string;
  processTitle: string;
  productProcessId?: string;
  show: number;
  pin: number;
  /** Photo view (FULL, PALLU…) the studio chose when adding the file. */
  view?: string;
  status: "QUEUED" | "UPLOADING" | "UPLOADED" | "FAILED";
  blob?: Blob;
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "mediaId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Could not open upload storage"));
  });

export const putUploadJob = async (job: StoredUploadJob): Promise<void> => {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Could not save upload"));
    tx.objectStore(STORE).put(job);
  });
  db.close();
};

export const getUploadJob = async (mediaId: string): Promise<StoredUploadJob | undefined> => {
  const db = await openDb();
  const row = await new Promise<StoredUploadJob | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(mediaId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("Could not read upload"));
  });
  db.close();
  return row;
};

export const allUploadJobs = async (): Promise<StoredUploadJob[]> => {
  const db = await openDb();
  const rows = await new Promise<StoredUploadJob[]>((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error || new Error("Could not list uploads"));
  });
  db.close();
  return rows;
};

export const deleteUploadJob = async (mediaId: string): Promise<void> => {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Could not clear upload"));
    tx.objectStore(STORE).delete(mediaId);
  });
  db.close();
};

export const clearAllUploadJobs = async (): Promise<void> => {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error("Could not clear uploads"));
    tx.objectStore(STORE).clear();
  });
  db.close();
};

